import * as React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectCoverflow, Pagination, Navigation, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import 'swiper/css/autoplay';
import natalia1 from "@/assets/natalia-1.webp";
import natalia2 from "@/assets/natalia-2.webp";
import natalia3 from "@/assets/natalia-3.webp";
import natalia4 from "@/assets/natalia-4.webp";
import natalia5 from "@/assets/natalia-5.webp";

const clinicImages = [
  {
    src: natalia1,
    alt: "Dra. Natália Piller em seu consultório com roupa laranja",
    title: "Dra. Natália Piller",
  },
  {
    src: natalia2,
    alt: "Dra. Natália Piller em ambiente profissional",
    title: "Atendimento Profissional",
  },
  {
    src: natalia3,
    alt: "Dra. Natália Piller em seu espaço de trabalho",
    title: "Smile & Beauty",
  },
  {
    src: natalia4,
    alt: "Dra. Natália Piller de jaleco branco",
    title: "Expertise Odontológica",
  },
  {
    src: natalia5,
    alt: "Dra. Natália Piller em retrato profissional",
    title: "Cuidado Personalizado",
  },
];

const extendedClinicImages = [...clinicImages, ...clinicImages, ...clinicImages];

const ClinicCarousel = () => {
  const swiperRef = React.useRef<any>(null);
  const [currentSlide, setCurrentSlide] = React.useState(1);
  const [totalSlides] = React.useState(clinicImages.length);
  
  React.useEffect(() => {
    swiperRef.current?.autoplay?.start?.();
  }, []);
  
  return (
    <section className="py-4 sm:py-5 lg:py-6" style={{ backgroundColor: '#F6F5F5' }}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-8 sm:mb-12 lg:mb-16 animate-fade-in-up">
          <p className="text-accent font-inter text-sm uppercase tracking-wider mb-3 inline-block px-4 py-2 rounded-full border border-accent/30 bg-accent/5">
            Conheça a Especialista
          </p>
          <h2 className="font-kiona text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mt-4 mb-4 uppercase">
            Dra. Natália Piller
          </h2>
          <p className="text-muted-foreground font-inter text-lg max-w-2xl mx-auto">
            Especialista em harmonização orofacial e odontologia estética com dedicação ao seu sorriso
          </p>
        </div>

        <div className="relative max-w-7xl mx-auto pb-6 sm:pb-8 lg:pb-12">
          <Swiper
            onSwiper={(swiper) => { swiperRef.current = swiper; }}
            onSlideChange={(swiper) => {
              const realIndex = swiper.realIndex % clinicImages.length;
              setCurrentSlide(realIndex + 1);
            }}
            effect={'coverflow'}
            grabCursor={true}
            centeredSlides={true}
            slidesPerView={'auto'}
            spaceBetween={24}
            watchSlidesProgress={true}
            loop={true}
            loopAdditionalSlides={6}
            
            coverflowEffect={{
              rotate: 50,
              stretch: 0,
              depth: 120,
              modifier: 1,
              slideShadows: true,
            }}
            pagination={{
              clickable: true,
              dynamicBullets: true,
            }}
            navigation={false}
            autoplay={{
              delay: 2500,
              disableOnInteraction: false,
              pauseOnMouseEnter: false,
            }}
            modules={[EffectCoverflow, Pagination, Navigation, Autoplay]}
            className="clinic-swiper"
            breakpoints={{
              320: {
                coverflowEffect: {
                  rotate: 30,
                  depth: 80,
                }
              },
              768: {
                coverflowEffect: {
                  rotate: 50,
                  depth: 120,
                }
              }
            }}
          >
            {extendedClinicImages.map((image, index) => (
              <SwiperSlide key={index} className="!w-[320px] md:!w-[450px] lg:!w-[550px]">
                <Card className="group bg-card/50 backdrop-blur-sm border-border/50 hover:border-accent/50 transition-all duration-500 hover:shadow-silver-lg overflow-hidden">
                  <CardContent className="p-0 relative aspect-[4/3]">
                    <div className="absolute inset-0 bg-gradient-silver-subtle opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10" />
                    <img
                      src={image.src}
                      alt={image.alt}
                      className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-110"
                      loading="lazy"
                    />
                    <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent z-20">
                      <h3 className="font-kiona text-xl font-semibold text-white uppercase">
                        {image.title}
                      </h3>
                    </div>
                  </CardContent>
                </Card>
              </SwiperSlide>
            ))}
          </Swiper>
          
          <div className="text-center mt-4 sm:mt-6 lg:mt-8">
            <p className="text-foreground font-inter text-lg font-semibold">
              {currentSlide} / {totalSlides}
            </p>
          </div>
        </div>
      </div>

      <style>{`
        .clinic-swiper {
          padding-top: 50px;
          padding-bottom: 50px;
          overflow: visible;
        }
        
        @media (max-width: 640px) {
          .clinic-swiper {
            padding-top: 25px;
            padding-bottom: 25px;
          }
        }
        
        .clinic-swiper .swiper-slide {
          background-position: center;
          background-size: cover;
        }
        
        .clinic-swiper .swiper-pagination-bullet {
          background: hsl(var(--accent));
          opacity: 0.5;
        }
        
        .clinic-swiper .swiper-pagination-bullet-active {
          opacity: 1;
        }
        
        .clinic-swiper .swiper-button-next,
        .clinic-swiper .swiper-button-prev {
          color: white;
          background: hsl(var(--primary));
          width: 48px;
          height: 48px;
          border-radius: 12px;
          border: none;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 4px 12px hsl(var(--primary) / 0.3);
          top: 50%;
        }
        
        .clinic-swiper .swiper-button-next:after,
        .clinic-swiper .swiper-button-prev:after {
          font-size: 18px;
          font-weight: bold;
        }
        
        .clinic-swiper .swiper-button-next:hover,
        .clinic-swiper .swiper-button-prev:hover {
          background: hsl(var(--primary) / 0.9);
          transform: scale(1.08);
          box-shadow: 0 6px 20px hsl(var(--primary) / 0.4);
        }
        
        .clinic-swiper .swiper-button-next:active,
        .clinic-swiper .swiper-button-prev:active {
          transform: scale(0.98);
        }
        
        .clinic-swiper .swiper-button-disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        
        @media (max-width: 768px) {
          .clinic-swiper .swiper-button-next,
          .clinic-swiper .swiper-button-prev {
            width: 40px;
            height: 40px;
          }
          
          .clinic-swiper .swiper-button-next:after,
          .clinic-swiper .swiper-button-prev:after {
            font-size: 14px;
          }
        }
      `}</style>
    </section>
  );
};

export default ClinicCarousel;
